import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Rocket, KeyRound, Apple, Smartphone } from 'lucide-react';
import Button from '../components/UI/Button';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface AppsFlyerFormData {
  devKey: string; // shared across the whole AppsFlyer account
  appsflyerEnabled: boolean; // per-app
  iosAppId: string; // per-app (numeric App Store ID)
  androidAppId: string; // per-app (package name)
}

const AppsFlyerSettings: React.FC = () => {
  const { currentFlavourId } = useAuth();
  const [existingRecord, setExistingRecord] = useState<any>(null); // per-flavour row
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AppsFlyerFormData>({
    defaultValues: {
      devKey: '',
      appsflyerEnabled: false,
      iosAppId: '',
      androidAppId: '',
    },
  });

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      // Account-level shared Dev Key (always fetch)
      const accountRes = await apiService.getAppsFlyerAccount();
      const devKey = accountRes?.data?.devKey ?? '';

      // Per-flavour routing
      let record: any = null;
      if (currentFlavourId) {
        const res = await apiService.getAppsFlyerSettings(currentFlavourId);
        record = res?.data ?? null;
      }
      setExistingRecord(record);
      reset({
        devKey,
        appsflyerEnabled: record?.appsflyerEnabled ?? false,
        iosAppId: record?.appsflyerAppIdIos ?? '',
        androidAppId: record?.appsflyerAppIdAndroid ?? '',
      });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load AppsFlyer settings.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlavourId]);

  const validateIosAppId = (value: string) => {
    if (!value) return true; // optional unless enabling
    return (
      /^\d+$/.test(value) ||
      'iOS App ID must be the numeric App Store ID (digits only), e.g. 6480000000.'
    );
  };

  const validateAndroidAppId = (value: string) => {
    if (!value) return true; // optional
    return (
      /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/.test(value) ||
      'Android App ID must be a package name, e.g. com.tour.scotland.'
    );
  };

  const onSubmit = async (formData: AppsFlyerFormData) => {
    // Mirror the backend invariant: cannot enable without a Dev Key + an App ID.
    if (formData.appsflyerEnabled) {
      if (!formData.devKey.trim()) {
        toast.error('Enter the shared Dev Key before enabling AppsFlyer.');
        return;
      }
      if (!formData.iosAppId && !formData.androidAppId) {
        toast.error(
          'Enter at least an iOS App ID (or Android App ID) before enabling.',
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // 1) Save the shared account Dev Key (applies to every app).
      await apiService.saveAppsFlyerAccount({ devKey: formData.devKey || '' });

      // 2) Save this app's routing (requires a selected flavour/app).
      if (currentFlavourId) {
        await apiService.saveAppsFlyerSettings(currentFlavourId, {
          appsflyerEnabled: formData.appsflyerEnabled,
          appsflyerAppIdIos: formData.iosAppId || '',
          appsflyerAppIdAndroid: formData.androidAppId || '',
        });
      }
      toast.success('AppsFlyer settings saved.');
      await fetchSettings();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save AppsFlyer settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentFlavourId || !existingRecord) return;
    if (
      !window.confirm(
        "Disable AppsFlyer and delete this app's settings? (The shared Dev Key is kept.)",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await apiService.deleteAppsFlyerSettings(currentFlavourId);
      toast.success('AppsFlyer settings deleted for this app.');
      setExistingRecord(null);
      await fetchSettings();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete AppsFlyer settings.');
    } finally {
      setIsDeleting(false);
    }
  };

  const inputBase =
    'w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
          <Rocket className="w-5 h-5 text-black" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AppsFlyer Integration</h1>
          <p className="text-sm text-gray-500">
            Turn AppsFlyer on for this app and point it at its AppsFlyer apps. The
            Dev Key is shared across every app in your AppsFlyer account.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Status badge */}
          <div className="mb-6">
            {existingRecord?.appsflyerEnabled ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Enabled for this app — use Update to save changes
              </span>
            ) : existingRecord ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Configured but disabled for this app
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                Not set up for this app yet
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* ── Account (shared) ─────────────────────────────── */}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-1">
                AppsFlyer Account (shared)
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                One Dev Key for your whole AppsFlyer account — set it once; it
                applies to every app.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-amber-500" />
                  Dev Key
                </span>
              </label>
              <input
                type="text"
                placeholder="Your AppsFlyer Dev Key"
                className={`${inputBase} ${
                  errors.devKey ? 'border-red-400' : 'border-gray-300'
                }`}
                {...register('devKey')}
              />
            </div>

            <div className="border-t border-gray-200" />

            {/* ── This app ─────────────────────────────────────── */}
            <div className="space-y-5">
              <h2 className="text-sm font-semibold text-gray-900">This app</h2>

              {/* Enable toggle */}
              <div className="flex items-center justify-between py-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    AppsFlyer Enabled
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When on, the app initializes AppsFlyer on next launch and starts
                    sending events.
                  </p>
                </div>
                <input
                  type="checkbox"
                  {...register('appsflyerEnabled')}
                  className="h-5 w-5 text-primary focus:ring-primary border-gray-300 rounded"
                />
              </div>

              {/* iOS App ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Apple className="w-4 h-4 text-gray-700" />
                    iOS App ID
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 6480000000 (numeric App Store ID)"
                  className={`${inputBase} ${
                    errors.iosAppId ? 'border-red-400' : 'border-gray-300'
                  }`}
                  {...register('iosAppId', { validate: validateIosAppId })}
                />
                {errors.iosAppId && (
                  <p className="mt-1 text-xs text-red-500">{errors.iosAppId.message}</p>
                )}
              </div>

              {/* Android App ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-green-600" />
                    Android App ID
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. com.tour.scotland (package name)"
                  className={`${inputBase} ${
                    errors.androidAppId ? 'border-red-400' : 'border-gray-300'
                  }`}
                  {...register('androidAppId', { validate: validateAndroidAppId })}
                />
                {errors.androidAppId && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.androidAppId.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  Optional — AppsFlyer auto-detects the package on Android.
                </p>
              </div>

              {!currentFlavourId && (
                <p className="text-xs text-amber-600">
                  Select an app first to save its per-app settings. (The Dev Key
                  above can still be saved.)
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Save
              </Button>

              {existingRecord && (
                <Button
                  type="button"
                  variant="danger"
                  loading={isDeleting}
                  disabled={isDeleting}
                  onClick={handleDelete}
                >
                  Delete this app's settings
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AppsFlyerSettings;
